import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  Space,
  message,
  Row,
  Col,
  Typography,
  DatePicker,
  InputNumber,
  Spin,
  Result,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const JobEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vans, setVans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [plPosition, setPlPosition] = useState('income');
  const [relatedJobs, setRelatedJobs] = useState([]);

  const fetchInitialData = useCallback(async () => {
    try {
      setError(null);
      
      // Fetch job details first to validate ID
      const jobResponse = await apiService.jobs.getById(id);
      if (!jobResponse.data) {
        throw new Error('Job not found');
      }
      
      const jobData = jobResponse.data;
      setPlPosition(jobData.plPosition);

      // Calculate opposite plPosition for related jobs
      const oppositePlPosition = jobData.plPosition === 'income' ? 'expense' : 'income';

      // Then fetch other data
      const [driversRes, vansRes, jobsRes, channelsRes] = await Promise.all([
        apiService.drivers.getAll(),
        apiService.vans.getAll(),
        apiService.jobs.getAllForDropdown({ plPosition: oppositePlPosition }),
        apiService.channels.getActive()
      ]);

      setDrivers(driversRes.data);
      setVans(vansRes.data);
      setChannels(channelsRes.data);
      
      // Filter jobs that don't have a related job and exclude current job
      const jobsArray = Array.isArray(jobsRes.data) ? jobsRes.data : [];
      const filteredJobs = jobsArray.filter(job => 
        !job.relatedJob && // Only show jobs that don't have a related job
        job._id !== id // Exclude current job
      );
      setRelatedJobs(filteredJobs);

      // Format dates for form
      const formattedJobData = {
        ...jobData,
        date: jobData.date ? dayjs(jobData.date) : null,
        collectionDetails: {
          ...jobData.collectionDetails,
          date: jobData.collectionDetails?.date ? dayjs(jobData.collectionDetails.date) : null,
          time: jobData.collectionDetails?.time ? 
            jobData.collectionDetails.time.padStart(5, '0') : '09:00'
        },
        delivery: jobData.delivery ? {
          ...jobData.delivery,
          date: jobData.delivery.date ? dayjs(jobData.delivery.date) : null,
          time: jobData.delivery.time ? 
            jobData.delivery.time.padStart(5, '0') : '17:00'
        } : undefined,
        payment: jobData.payment ? {
          ...jobData.payment,
          dueDate: jobData.payment.dueDate ? dayjs(jobData.payment.dueDate) : null
        } : undefined,
        // Set driver and van IDs directly from their objects
        driver: jobData.driver?._id || jobData.driver,
        van: jobData.van?._id || jobData.van,
        channel: (typeof jobData.channel === 'object' && jobData.channel.name) ? jobData.channel.name : jobData.channel,
        // Ensure relatedJob is set properly
        relatedJob: jobData.relatedJob?._id || jobData.relatedJob
      };

      form.setFieldsValue(formattedJobData);

      // If there's a related job, fetch its details to populate the form
      if (jobData.relatedJob?._id) {
        const relatedJobResponse = await apiService.jobs.getById(jobData.relatedJob._id);
        const relatedJob = relatedJobResponse.data;
        if (relatedJob) {
          // Update relevant fields based on the related job
          form.setFieldsValue({
            customer: relatedJob.customer,
            collectionDetails: {
              ...relatedJob.collectionDetails,
              date: relatedJob.collectionDetails?.date ? dayjs(relatedJob.collectionDetails.date) : null
            },
            delivery: relatedJob.delivery ? {
              ...relatedJob.delivery,
              date: relatedJob.delivery.date ? dayjs(relatedJob.delivery.date) : null
            } : undefined
          });
        }
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load job data');
      message.error('Failed to load job data');
    }
  }, [id, form]);

  useEffect(() => {
    if (!id || id.length !== 24) {
      setError('Invalid job ID');
      return;
    }
    fetchInitialData();
  }, [id, fetchInitialData]);

  const calculateVAT = (amount, vatRate) => {
    return (amount * vatRate) / 100;
  };

  const calculateTotal = (amount, vatAmount) => {
    return amount + vatAmount;
  };

  const handleAmountChange = (value) => {
    // Only calculate if value exists and is not 0
    if (value && value > 0) {
      const vatRate = form.getFieldValue(['payment', 'vatRate']);
      // Only calculate if we have a valid VAT rate
      if (vatRate !== undefined && vatRate !== null) {
        const vatAmount = calculateVAT(value, vatRate);
        const totalAmount = calculateTotal(value, vatAmount);
        
        form.setFieldsValue({
          payment: {
            vatAmount: vatAmount,
            totalAmount: totalAmount
          }
        });
      }
    } else {
      // Reset VAT and total amounts if amount is 0 or cleared
      form.setFieldsValue({
        payment: {
          vatAmount: 0,
          totalAmount: 0
        }
      });
    }
  };

  const handleVatRateChange = (value) => {
    const amount = form.getFieldValue(['payment', 'amount']);
    // Only calculate if amount exists and is not 0
    if (amount && amount > 0) {
      const vatAmount = calculateVAT(amount, value);
      const totalAmount = calculateTotal(amount, vatAmount);
      
      form.setFieldsValue({
        payment: {
          vatAmount: vatAmount,
          totalAmount: totalAmount
        }
      });
    } else {
      // Reset VAT and total amounts if no amount is entered
      form.setFieldsValue({
        payment: {
          vatAmount: 0,
          totalAmount: 0
        }
      });
    }
  };

  const handleJobStatusChange = (value) => {
    // Just update the form value without making an API call
    form.setFieldsValue({
      jobStatus: value
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Use the selected job status from the form
      const jobStatus = values.jobStatus;

      // Format dates and restructure payment data
      const formattedValues = {
        ...values,
        jobStatus: jobStatus, // Use the selected job status
        date: values.date?.toISOString(),
        collectionDetails: {
          ...values.collectionDetails,
          date: values.collectionDetails?.date?.toISOString()
        },
        delivery: values.delivery ? {
          ...values.delivery,
          date: values.delivery.date?.toISOString()
        } : undefined,
        // Ensure driver and van are sent as IDs
        driver: values.driver?._id || values.driver,
        van: values.van?._id || values.van,
        // Explicitly set relatedJob to null if it's undefined or empty
        relatedJob: values.relatedJob || null,
        // Include payment data in the job update
        payment: values.payment ? {
          ...values.payment,
          dueDate: values.payment.dueDate?.toISOString(),
          amount: parseFloat(values.payment.amount),
          vatRate: parseFloat(values.payment.vatRate),
          vatAmount: parseFloat(values.payment.vatAmount),
          totalAmount: parseFloat(values.payment.totalAmount),
          status: values.payment.status,
          paymentMethod: values.payment.paymentMethod,
          notes: values.payment.notes,
          plPosition: plPosition
        } : undefined
      };

      // Update the job with all changes including payment data
      await apiService.jobs.update(id, formattedValues);
      
      message.success('Job updated successfully');
      navigate(`/jobs/${id}`);
    } catch (error) {
      console.error('Error updating job:', error);
      message.error(error.response?.data?.message || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Result
          status="error"
          title="Error"
          subTitle={error}
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/jobs')}>
              Back to Jobs
            </Button>
          ]}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>Edit Job</span>
          </Space>
        }
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/jobs')}
          >
            Back
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Title level={4}>Job Details</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reference"
                label="Reference"
                rules={[{ required: true, message: 'Please enter reference' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'name']}
                label="Customer Name"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'email']}
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'phone']}
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="plPosition"
                label="PL Position"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="relatedJob"
                label="Link to Related Job"
                tooltip="Link this job to another job with opposite PL position"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('plPosition') === 'expense' && !value) {
                        return Promise.reject(new Error('Link to Related Job is required for expense jobs'));
                      }
                      return Promise.resolve();
                    },
                  })
                ]}
              >
                <Select
                  allowClear
                  placeholder="Select related job"
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => {
                    // When a related job is selected, fetch its details to update the form
                    if (value) {
                      apiService.jobs.getById(value).then(response => {
                        const relatedJob = response.data;
                        // Update relevant fields based on the related job
                        form.setFieldsValue({
                          customer: relatedJob.customer,
                          collectionDetails: {
                            ...relatedJob.collectionDetails,
                            date: relatedJob.collectionDetails?.date ? dayjs(relatedJob.collectionDetails.date) : null
                          },
                          delivery: relatedJob.delivery ? {
                            ...relatedJob.delivery,
                            date: relatedJob.delivery.date ? dayjs(relatedJob.delivery.date) : null
                          } : undefined
                        });
                      });
                    }
                  }}
                >
                  {relatedJobs.map(job => (
                    <Option key={job._id} value={job._id}>
                      {job.bookingReference || job.reference || 'No Reference'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="plDescription"
                label="PL Description"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="jobStatus"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select onChange={handleJobStatusChange}>
                  <Option value="unassigned">Unassigned</Option>
                  <Option value="assigned">Assigned</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="driver"
                label="Driver"
                tooltip="Assign a driver to this job"
              >
                <Select
                  allowClear
                  placeholder="Select driver"
                  showSearch
                  optionFilterProp="children"
                >
                  {drivers.map(driver => (
                    <Option key={driver._id} value={driver._id}>
                      {driver.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="van"
                label="Vehicle"
                tooltip="Assign a vehicle to this job"
              >
                <Select
                  allowClear
                  placeholder="Select vehicle"
                  showSearch
                  optionFilterProp="children"
                >
                  {vans.map(van => (
                    <Option key={van._id} value={van._id}>
                      {van.registration} ({van.make} {van.model})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="bookingReference"
                label="External Reference"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="channel"
                label="Channel"
              >
                <Select
                  allowClear
                  placeholder="Select channel"
                  showSearch
                  optionFilterProp="children"
                >
                  {channels.map(channel => (
                    <Option key={channel._id} value={channel.name}>
                      {channel.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="jobRequirement"
                label="Job Requirement"
                rules={[{ required: true, message: 'Please select job requirement' }]}
              >
                <Select>
                  <Option value="one-man">One Man</Option>
                  <Option value="two-man">Two Man</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>Collection Details</Title>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['collectionDetails', 'date']}
                label="Collection Date"
                rules={[{ required: true, message: 'Please select collection date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['collectionDetails', 'time']}
                label="Collection Time"
                rules={[{ required: true, message: 'Please enter collection time' }]}
              >
                <Input type="time" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['collectionDetails', 'address']}
                label="Collection Address"
                rules={[{ required: true, message: 'Please enter collection address' }]}
              >
                <TextArea rows={3} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['collectionDetails', 'postcode']}
                label="Collection Postcode"
                rules={[{ required: true, message: 'Please enter collection postcode' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>Delivery Details</Title>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['delivery', 'date']}
                label="Delivery Date"
                rules={[{ required: true, message: 'Please select delivery date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['delivery', 'time']}
                label="Delivery Time"
                rules={[{ required: true, message: 'Please enter delivery time' }]}
              >
                <Input type="time" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['delivery', 'address']}
                label="Delivery Address"
                rules={[{ required: true, message: 'Please enter delivery address' }]}
              >
                <TextArea rows={3} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['delivery', 'postcode']}
                label="Delivery Postcode"
                rules={[{ required: true, message: 'Please enter delivery postcode' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            {plPosition === 'income' && (
              <>
                <Col span={24}>
                  <Title level={4}>Payment Details</Title>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name={['payment', 'reference']}
                    label="Invoice Number"
                    rules={[{ required: true, message: 'Please enter invoice number' }]}
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name={['payment', 'dueDate']}
                    label="Payment Due Date"
                    rules={[{ required: true, message: 'Please select payment due date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'amount']}
                    label="Amount (£)"
                    rules={[{ required: true, message: 'Please enter amount' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      prefix={<DollarOutlined />}
                      onChange={handleAmountChange}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'vatRate']}
                    label="VAT Rate (%)"
                    rules={[{ required: true, message: 'Please enter VAT rate' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      step={0.1}
                      precision={1}
                      onChange={handleVatRateChange}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'vatAmount']}
                    label="VAT Amount (£)"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled
                      precision={2}
                      prefix={<DollarOutlined />}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'totalAmount']}
                    label="Total Amount (£)"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled
                      precision={2}
                      prefix={<DollarOutlined />}
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'status']}
                    label="Payment Status"
                    rules={[{ required: true, message: 'Please select payment status' }]}
                  >
                    <Select>
                      <Option value="pending">Pending</Option>
                      <Option value="paid">Paid</Option>
                      <Option value="overdue">Overdue</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    name={['payment', 'paymentMethod']}
                    label="Payment Method"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                  >
                    <Select>
                      <Option value="bank_transfer">Bank Transfer</Option>
                      <Option value="credit_card">Credit Card</Option>
                      <Option value="debit_card">Debit Card</Option>
                      <Option value="cash">Cash</Option>
                      <Option value="check">Check</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={['payment', 'notes']}
                    label="Payment Notes"
                  >
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col span={24}>
              <Form.Item>
                <Space>
                  <Button onClick={() => navigate('/jobs')}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    Save Changes
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default JobEdit; 